// v10/src/components/admin/fuel/DeleteTankConfirmModal.js
//
// Modal de confirmación para soft delete de un tanque.
// DELETE /api/fuel-tanks/:id (el backend ya hace soft delete con is_active=false).

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Loader, AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import fuelService from '../../../services/fuelService';

const DeleteTankConfirmModal = ({ tank, onSuccess, onClose, darkMode }) => {
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const handleDelete = async () => {
    if (!tank?.id || loading) return;
    setLoading(true); setError('');
    try {
      await fuelService.deleteTank(tank.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || t('fuel_validation_network_error'));
      setLoading(false);
    }
  };

  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 460, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
          <button
            type="button" onClick={onClose} disabled={loading}
            aria-label="close"
            style={{
              padding: 6, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: loading ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(239,68,68,0.1)',
              border: '2px solid rgba(239,68,68,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
            }}
          >
            <AlertTriangle style={{ width: 26, height: 26, color: '#ef4444' }} />
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: fgPrimary }}>
            {t('fuel_delete_modal_title')}
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: fgMuted, lineHeight: 1.5 }}>
            {t('fuel_delete_modal_question', { name: tank?.name || '' })}
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: fgMuted, lineHeight: 1.5 }}>
            {t('fuel_delete_modal_explanation')}
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button" onClick={onClose} disabled={loading}
            style={{
              flex: 1, padding: 13, borderRadius: 14,
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
              background: 'transparent',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 14, color: fgMuted,
              fontFamily: 'inherit',
            }}
          >
            {t('fuel_modal_btn_cancel')}
          </button>
          <motion.button
            type="button" onClick={handleDelete} disabled={loading}
            whileHover={!loading ? { scale: 1.02 } : {}}
            whileTap={!loading ? { scale: 0.97 } : {}}
            style={{
              flex: 1, padding: 13, borderRadius: 14, border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg,#dc2626,#ef4444)',
              color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
            {loading
              ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
              : <Trash2 style={{ width: 16, height: 16 }} />
            }
            {loading ? t('fuel_delete_modal_btn_deleting') : t('fuel_delete_modal_btn_confirm')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DeleteTankConfirmModal;
